import {RegionEntity, getFormattedTimeZone, getFormattedDateTime} from "@ots-frontend/ots-data";
import {
  Badge,
  Dropdown,
  DropdownButton,
  DropdownDescription,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
  NavbarItem,
  NavbarLabel,
} from "catalyst";
import {
  ChevronDownIcon,
  Cog6ToothIcon,
  GlobeAmericasIcon,
} from "@heroicons/react/16/solid";
import clsx from "clsx";
import {useApp} from "../app/AppContext";
import {getOtsThemeVariables, useOtsBrand, useOtsColorScheme} from "../theme/OtsTheme";

export type OtsRegionSwitcherProps = {
  currentRegion?: RegionEntity;
  regions?: RegionEntity[];
  canSwitchRegion: boolean;
  canAccessGlobal?: boolean;
  onRegionChange: (region: RegionEntity | undefined) => void;
  onConfigureClick?: () => void;
  formatDateTime?: (dttm: number) => string;
  variant?: "navbar" | "sidebar";
  /** Tighter rows in the region menu; the button is unchanged. */
  compact?: boolean;
  className?: string;
};

const regionName = (region: RegionEntity): string => region.geographicName ?? region.name;

const regionLabel = (region: RegionEntity): string =>
  `${regionName(region)} (${getFormattedTimeZone(region.timeZone)})`;

const SidebarRegionLabel = ({region, fallback}: {region?: RegionEntity; fallback: string}) => (
  <span className="min-w-0 flex-1 leading-4">
    <span className="block whitespace-normal break-words text-xs font-bold">
      {region ? regionName(region) : fallback}
    </span>
    {region && (
      <span className="mt-0.5 block text-[10px] font-semibold leading-3 text-zinc-500 dark:text-zinc-400">
        {getFormattedTimeZone(region.timeZone)}
      </span>
    )}
  </span>
);

const ActiveRegionBadge = () => (
  <Badge
    className={clsx(
      "col-start-5 row-start-1 justify-self-end",
      "!bg-[var(--ots-primary-container)] !text-[var(--ots-primary)]",
      "group-data-focus:!bg-[var(--ots-on-primary)] group-data-focus:!text-[var(--ots-primary)]",
    )}>
    Active
  </Badge>
);

const brandedMenuItemClass = clsx(
  "data-focus:!bg-[var(--ots-primary)] data-focus:!text-[var(--ots-on-primary)]",
  "data-focus:*:data-[slot=icon]:!text-[var(--ots-on-primary)]",
);

/** Shared region selector for the WaterGo top navigation and Go-app sidebars. */
export const OtsRegionSwitcher = ({
  currentRegion,
  regions = [],
  canSwitchRegion,
  canAccessGlobal = false,
  onRegionChange,
  onConfigureClick,
  formatDateTime = getFormattedDateTime,
  variant = "navbar",
  compact = false,
  className,
}: OtsRegionSwitcherProps) => {
  const app = useApp();
  const enclosingBrand = useOtsBrand();
  const enclosingColorScheme = useOtsColorScheme();
  const themeVariables = getOtsThemeVariables(
    enclosingBrand ?? app?.brand ?? "corporate",
    enclosingColorScheme ?? (app?.colorScheme === "dark" ? "dark" : "light"),
  );
  const currentLabel = currentRegion
    ? regionLabel(currentRegion)
    : canAccessGlobal
      ? "Global"
      : "No region";

  // Compact lists the regions in tighter rows, for long lists in a navbar; the button stays as it is.
  const menuItemClass = clsx(
    brandedMenuItemClass,
    compact && "!py-1 !text-xs/5 *:data-[slot=icon]:!size-4 *:data-[slot=description]:!text-[0.6875rem]/4",
  );

  const content = (
    <>
      <GlobeAmericasIcon/>
      {variant === "navbar"
        ? <NavbarLabel>{currentLabel}</NavbarLabel>
        : <SidebarRegionLabel region={currentRegion} fallback={currentLabel}/>}
    </>
  );

  if (!canSwitchRegion) {
    return variant === "navbar" ? (
      <NavbarItem className={className} style={themeVariables}>{content}</NavbarItem>
    ) : (
      <div
        style={themeVariables}
        className={clsx(
          "flex w-full items-center gap-3 px-4 py-2.5 text-left text-zinc-700 dark:text-zinc-100",
          "[&>[data-slot=icon]]:size-5 [&>[data-slot=icon]]:shrink-0 [&>[data-slot=icon]]:text-zinc-500",
          className,
        )}>
        {content}
      </div>
    );
  }

  return (
    <Dropdown>
      {variant === "navbar" ? (
        <DropdownButton as={NavbarItem} className={className} style={themeVariables}>
          {content}
          <ChevronDownIcon/>
        </DropdownButton>
      ) : (
        <DropdownButton
          as="button"
          style={themeVariables}
          className={clsx(
            "flex w-full items-center gap-3 px-4 py-2.5 text-left text-zinc-700",
            "hover:!bg-[var(--ots-primary-container)] focus:outline-none data-active:!bg-[var(--ots-primary-container)]",
            "dark:text-zinc-100",
            "[&>[data-slot=icon]]:size-5 [&>[data-slot=icon]]:shrink-0 [&>[data-slot=icon]]:text-zinc-500",
            "[&>[data-slot=icon]:last-child]:ml-auto [&>[data-slot=icon]:last-child]:size-4",
            className,
          )}>
          {content}
          <ChevronDownIcon/>
        </DropdownButton>
      )}
      <DropdownMenu
        style={themeVariables}
        className={clsx("z-[1001] max-w-[calc(100vw-1rem)] sm:max-w-96", compact ? "min-w-56" : "min-w-64")}
        anchor="bottom start">
        {canAccessGlobal && (
          <DropdownItem
            className={menuItemClass}
            aria-current={!currentRegion ? "true" : undefined}
            onClick={() => onRegionChange(undefined)}>
            <GlobeAmericasIcon/>
            <DropdownLabel>Global</DropdownLabel>
            {!currentRegion && <ActiveRegionBadge/>}
          </DropdownItem>
        )}
        {regions.map((region) => {
          const isActive = region.id === currentRegion?.id;

          return (
            <DropdownItem
              key={region.id}
              className={menuItemClass}
              aria-current={isActive ? "true" : undefined}
              onClick={() => onRegionChange(region)}>
              <GlobeAmericasIcon/>
              <DropdownLabel className="whitespace-normal break-words">
                {regionLabel(region)}
              </DropdownLabel>
              {isActive && <ActiveRegionBadge/>}
              {region.lockDownDttm && (
                <DropdownDescription>Lockdown: {formatDateTime(region.lockDownDttm)}</DropdownDescription>
              )}
            </DropdownItem>
          );
        })}
        {onConfigureClick && (
          <>
            <DropdownDivider/>
            <DropdownItem className={menuItemClass} onClick={onConfigureClick}>
              <Cog6ToothIcon/>
              <DropdownLabel>Configure regions</DropdownLabel>
            </DropdownItem>
          </>
        )}
      </DropdownMenu>
    </Dropdown>
  );
};
